import { take, switchMap, catchError, finalize, map } from 'rxjs/operators';
import { AbstractControl, AsyncValidator, ValidationErrors } from '@angular/forms';
import { Observable, timer, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { getBusinessLine, getJobsiteState, getPurchaseOrderState, OrderBuilderState } from '../../../../reducers';
import { PurchaseOrderService } from '@cemex-core/cmx-api';
import {
  PurchaseOrderValidateStartAction,
  PurchaseOrderValidateEndAction,
} from '../../actions/purchase-order-actions/purchase-order.actions';

const getPurchaseOrderValidationData = createSelector(
  getBusinessLine,
  getJobsiteState,
  getPurchaseOrderState,
  (businessLineState, jobsiteState, purchaseOrderState) => {
    return {
      productLine: businessLineState.selected,
      jobsite: jobsiteState.selected,
      isValidationRequired: purchaseOrderState.required,
    };
  },
);

@Injectable()
export class PurchaseOrderValidator implements AsyncValidator {
  /**
   * Milliseconds to wait for the user to type again before starting validation.
   */
  public static typingDelay = 1250;

  constructor(
    private store: Store<OrderBuilderState>,
    private purchaseOrderService: PurchaseOrderService,
  ) {}

  /**
   * Calls the API to validate the content of the purchase-order field.
   * @param {AbstractControl} control
   * @return {Promise<ValidationErrors | null> | Observable<ValidationErrors | null>}
   */
  validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return this.store.select(getPurchaseOrderValidationData).pipe(
      take(1),
      switchMap((data) => {
        const noControl = control.value === undefined || control.value === '';
        const noValidation = !data.isValidationRequired;
        const noJobOrProduct = data.jobsite === null || data.productLine === null;

        if (noControl || noValidation || noJobOrProduct) {
          // Validator: form incomplete/no validation needed.
          return of(null);
        }

        return timer(PurchaseOrderValidator.typingDelay).pipe(
          switchMap(() => {
            this.store.dispatch(new PurchaseOrderValidateStartAction());
            return this.purchaseOrderService
              .validateV4({
                purchaseOrder: control.value,
                jobsiteId: data.jobsite.shipmentLocationId,
                productLine: data.productLine.productTypeCode,
              })
              .pipe(
                map((poValidation) => {
                  if (poValidation.hasOwnProperty('messageType')) {
                    // Passes validation on S for Success.
                    return poValidation['messageType'] === 'S' ? null : { purchaseOrderInvalid: true };
                  }
                  return null;
                }),
              );
          }),
          catchError((oops) => {
            console.warn(oops);
            return of({ purchaseOrderInvalid: true });
          }),
          finalize(() => {
            this.store.dispatch(new PurchaseOrderValidateEndAction());
          }),
        );
      }),
    );
  }
}
